"use client";

import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import {
  RocketLaunch,
  Groups,
  Business,
  EmojiObjects,
  Build,
  TrendingUp,
} from "@mui/icons-material";

export const runtime = "edge";

const values = [
  {
    icon: <EmojiObjects sx={{ fontSize: 40 }} />,
    title: "Innovation",
    description:
      "We embrace cutting-edge technologies and creative thinking to solve complex challenges.",
  },
  {
    icon: <Build sx={{ fontSize: 40 }} />,
    title: "Excellence",
    description:
      "We deliver high-quality solutions and maintain the highest standards in everything we do.",
  },
  {
    icon: <Groups sx={{ fontSize: 40 }} />,
    title: "Collaboration",
    description:
      "We believe in the power of teamwork and building strong partnerships with our clients.",
  },
  {
    icon: <TrendingUp sx={{ fontSize: 40 }} />,
    title: "Impact",
    description:
      "We measure success by the positive change we create for our clients and community.",
  },
];

export default function AboutUs() {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section with Video Background */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "70vh", md: "80vh" },
          minHeight: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          mb: 8,
        }}
      >
        {/* Video Background */}
        <video
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/aboutVid.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark Overlay for Better Text Readability */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.3
            )} 0%, ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Box textAlign="center" sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                color: "white",
                mb: 3,
                lineHeight: 1.2,
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
              }}
            >
              Empowering talents. Enabling entrepreneurs. Elevating businesses.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
                fontWeight: 400,
                color: "white",
                textShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              Building futures through technology, collaboration, and innovation
            </Typography>
          </Box>
        </Container>

        {/* Scroll Indicator */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 50,
              border: "2px solid white",
              borderRadius: 15,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              paddingTop: 1,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 10,
                backgroundColor: "white",
                borderRadius: 2,
                animation: "scroll 2s infinite",
                "@keyframes scroll": {
                  "0%": { transform: "translateY(0)", opacity: 1 },
                  "50%": { transform: "translateY(10px)", opacity: 0.7 },
                  "100%": { transform: "translateY(0)", opacity: 1 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg">
        {/* Introduction Section */}
        <Box sx={{ mb: 12 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 700,
                  mb: 3,
                  color: theme.palette.primary.main,
                }}
              >
                About LinXar Labs
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                At LinXar Labs, we believe technology is more than
                code—it&apos;s a catalyst for transformation.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                }}
              >
                We are a community of builders, dreamers, and problem-solvers
                dedicated to empowering talents, entrepreneurs, and SMEs with
                the digital tools and opportunities they need to thrive in
                today&apos;s connected world.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  borderRadius: 4,
                  p: 4,
                  color: "white",
                  textAlign: "center",
                }}
              >
                <RocketLaunch sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Innovation Hub
                </Typography>
                <Typography>
                  Where ideas meet execution and dreams become digital reality
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "2px",
            background: `linear-gradient(90deg, transparent 0%, ${alpha(
              theme.palette.primary.main,
              0.3
            )} 50%, transparent 100%)`,
            my: 8,
          }}
        />

        {/* Our Story Section */}
        <Box sx={{ mb: 12 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.secondary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                  borderRadius: 4,
                  p: 4,
                  height: "100%",
                }}
              >
                <Business
                  sx={{
                    fontSize: 48,
                    color: theme.palette.secondary.main,
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                >
                  Our Story
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                Founded in Accra, Ghana, LinXar Labs was built with the vision
                of helping remote businesses access elite automation and web
                development at predictable cost. Today we serve European SMEs,
                agencies and startups—delivering digital transformation one
                workflow at a time.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                }}
              >
                Though young, our journey has already shaped collaborations,
                inspired change, and earned trust as partners for those ready to
                embrace the digital future.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Vision & Mission Section */}
        <Grid container spacing={6} sx={{ mb: 12 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: "100%",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 4,
                p: 4,
              }}
            >
              <CardContent>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <EmojiObjects
                    sx={{ fontSize: 48, color: theme.palette.primary.main }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                  }}
                >
                  Our Vision
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.7,
                    color: "text.secondary",
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  A world where teams focus on growth—not on admin.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: "100%",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.secondary.main,
                  0.05
                )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                borderRadius: 4,
                p: 4,
              }}
            >
              <CardContent>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <RocketLaunch
                    sx={{ fontSize: 48, color: theme.palette.secondary.main }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: theme.palette.secondary.main,
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    color: "text.secondary",
                    textAlign: "center",
                  }}
                >
                  Empower businesses to scale: by removing manual work, building
                  smart web systems and automating processes.
                </Typography>
                <Box component="ul" sx={{ mt: 2, pl: 2 }}>
                  <Typography
                    component="li"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    By removing manual work
                  </Typography>
                  <Typography
                    component="li"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Building web systems and automating processes
                  </Typography>
                  <Typography component="li" sx={{ color: "text.secondary" }}>
                    Build digital solutions that help SMEs grow, compete, and
                    lead
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    color: "text.secondary",
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  Together, we&apos;re not just building technology — we&apos;re
                  building futures.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Our Passion Section */}
        <Box sx={{ mb: 12 }}>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Our Passion
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto", mt: 2 }}
            >
              What drives us is simple: impact. We are more than service
              providers
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  textAlign: "center",
                  p: 4,
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <Build
                  sx={{
                    fontSize: 48,
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Builders
                </Typography>
                <Typography color="text.secondary">
                  Creating solutions that solve real problems with innovative
                  technology and thoughtful design.
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  textAlign: "center",
                  p: 4,
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(
                      theme.palette.secondary.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <TrendingUp
                  sx={{
                    fontSize: 48,
                    color: theme.palette.secondary.main,
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Enablers
                </Typography>
                <Typography color="text.secondary">
                  Providing opportunities and access to digital growth through
                  mentorship and resources.
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  textAlign: "center",
                  p: 4,
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <Groups
                  sx={{
                    fontSize: 48,
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Changemakers
                </Typography>
                <Typography color="text.secondary">
                  Driving innovation that leaves lasting footprints in
                  businesses and communities.
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Box textAlign="center" sx={{ mt: 4 }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                color: "text.secondary",
                fontStyle: "italic",
                maxWidth: 600,
                mx: "auto",
              }}
            >
              For every talent we mentor, every entrepreneur we empower, and
              every SME we elevate, we move one step closer to a future where
              technology transforms lives.
            </Typography>
          </Box>
        </Box>

        {/* Our Values Section */}
        <Box sx={{ mb: 12 }}>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Our Values
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto", mt: 2 }}
            >
              The principles that guide everything we do
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 4,
                    height: "100%",
                    transition: "all 0.3s ease",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 30px ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    },
                  }}
                >
                  <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                    {value.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: "center",
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Build the Future Together?
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
          >
            Whether you&apos;re a talent looking to grow, an entrepreneur with a
            vision, or a business ready to scale, we&apos;re here to help you
            succeed.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Chip
              label="Join Our Talent Network"
              clickable
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            />
            <Chip
              label="Start Your Project"
              clickable
              sx={{
                backgroundColor: theme.palette.secondary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: theme.palette.secondary.dark,
                },
              }}
            />
            <Chip
              label="Partner With Us"
              clickable
              variant="outlined"
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
